/** 通用诉讼台账默认四表：案件总表、案件背景、程序与期限、进展记录。 */

import type {LitigationPlanFieldMapping, LitigationPlanStandardField} from './litigationLedgerPlan.js';

export type LitigationSheetKind = 'cases' | 'parties' | 'procedure' | 'progress';

export type LitigationSheetField = LitigationPlanStandardField & {
  sheetKind: LitigationSheetKind;
};

export type LitigationSheetSpec = {
  kind: LitigationSheetKind;
  name: string;
  fieldIds: string[];
};

function field(
  id: string,
  label: string,
  aliases: string[],
  group: LitigationPlanStandardField['group'],
  sheetKind: LitigationSheetKind,
  listDefault = false,
): LitigationSheetField {
  return {id, label, aliases, group, sheetKind, listDefault};
}

/** 明细表专用字段（主键内部案号复用总表字段）。 */
export const LITIGATION_DETAIL_FIELDS: readonly LitigationSheetField[] = [
  field('caseBackground', '内容介绍', ['内容介绍', '案件内容介绍', '背景介绍', '案件介绍'], 'basic', 'parties'),
  field('partyRole', '角色', ['角色', '当事人角色', '地位'], 'party', 'parties'),
  field('partyName', '名称', ['名称', '当事人', '当事人名称'], 'party', 'parties'),
  field('partyPrimary', '是否主当事人', ['是否主当事人', '主当事人', '主对方', '主我方'], 'party', 'parties'),
  field('partyContact', '联系方式', ['联系方式', '联系人', '电话'], 'party', 'parties'),
  field('nodeName', '节点名称', ['节点名称', '节点', '阶段名称'], 'procedure', 'procedure'),
  field('plannedDate', '计划日', ['计划日', '计划日期', '期限日'], 'procedure', 'procedure'),
  field('completedDate', '完成日', ['完成日', '完成日期'], 'procedure', 'procedure'),
  field('keyDeadline', '是否关键期限', ['是否关键期限', '关键期限'], 'procedure', 'procedure'),
  field('deadlineKind', '期限种类', ['期限种类', '期限类型'], 'procedure', 'procedure'),
  field('nodeNote', '备注', ['备注', '节点备注'], 'procedure', 'procedure'),
  field('progressDate', '日期', ['日期', '进展日期'], 'procedure', 'progress'),
  field('progressContent', '内容', ['内容', '进展内容', '进展'], 'procedure', 'progress'),
  field('progressAuthor', '记录人', ['记录人', '填写人'], 'meta', 'progress'),
];

/** 管理后台/系统标准四表 Excel 模板的表头行（1-based，客户端不可改）。 */
export const PLATFORM_LITIGATION_TEMPLATE_HEADER_ROW = 1;

/** 管理后台/系统标准四表 Excel 模板的数据起始行（1-based，客户端不可改）。 */
export const PLATFORM_LITIGATION_TEMPLATE_DATA_START_ROW = 2;

/** 默认四表名称与列顺序。 */
export const LITIGATION_DEFAULT_SHEETS: readonly LitigationSheetSpec[] = [
  {
    kind: 'cases',
    name: '案件总表',
    fieldIds: [
      'seqNo', 'internalNo', 'organizationUnit', 'caseName', 'caseNature', 'disputeType',
      'filingDate', 'involvedAmount', 'title', 'instance', 'courtNo', 'venue', 'claimSummary',
      'plaintiffParty', 'opponent', 'thirdParty', 'currentStage', 'closedAt', 'closeMode',
      'resultSummary', 'awardedAmount', 'latestProgress', 'relatedCourtNo', 'counsel', 'counselOrg',
    ],
  },
  {
    kind: 'parties',
    name: '案件背景',
    fieldIds: ['caseName', 'internalNo', 'caseBackground'],
  },
  {
    kind: 'procedure',
    name: '程序与期限',
    fieldIds: ['internalNo', 'nodeName', 'plannedDate', 'completedDate', 'keyDeadline', 'deadlineKind', 'nodeNote'],
  },
  {
    kind: 'progress',
    name: '进展记录',
    fieldIds: ['internalNo', 'progressDate', 'progressContent', 'progressAuthor'],
  },
];

const detailById = new Map(LITIGATION_DETAIL_FIELDS.map((item) => [item.id, item]));

/**
 * 按表类型取可映射字段（含总表的内部案号）。
 * @param kind 表类型
 * @param caseFields 总表标准字段
 */
export function litigationFieldsForSheetKind(
  kind: LitigationSheetKind,
  caseFields: readonly LitigationPlanStandardField[],
): LitigationPlanStandardField[] {
  const spec = LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === kind);
  if (!spec) return [...caseFields];
  return spec.fieldIds.map((id) => {
    const detail = detailById.get(id);
    if (detail) return detail;
    const fromCase = caseFields.find((item) => item.id === id);
    return fromCase ? {...fromCase, sheetKind: 'cases' as const} : {id, label: id, aliases: [id], group: 'basic'};
  });
}

/**
 * 根据工作表名称判断四表类型。
 *
 * 认不出来时返回 **null**，而不是默认成案件总表——否则「案件背景介绍」这类
 * 自定义工作表会被误判为案件总表，进而被套上整套标准字段目录。
 * 需要默认值的调用方请显式写 `?? 'cases'`。
 *
 * @param sheetName 工作表名
 */
export function detectLitigationSheetKind(sheetName: string): LitigationSheetKind | null {
  const key = sheetName.replace(/\s+/g, '');
  // 进展/程序/期限优先，避免被"案件"二字误判为总表
  if (/程序|期限|节点/.test(key)) return 'procedure';
  if (/进展|记录/.test(key) && !/总表/.test(key)) return 'progress';
  // 当事人表：当事/对方/我方主体，以及"案件背景介绍"这类承载当事人信息的表。
  // 注意不能简单用"含案件就排除"——用户的表名往往就叫「案件背景介绍」。
  if (/当事|对方|我方主体|背景|联系人/.test(key) && !/总表/.test(key)) return 'parties';
  if (/总表|案件|明细|台账/.test(key)) return 'cases';
  return null;
}

/**
 * 默认四表映射（按固定列顺序）。
 * @param caseFields 总表字段
 */
export function defaultLitigationSheetMaps(
  caseFields: readonly LitigationPlanStandardField[],
): Array<{
  sheetKind: LitigationSheetKind;
  sheetName: string;
  headerRow: number;
  dataStartRow: number;
  exportFields: LitigationPlanFieldMapping[];
}> {
  return LITIGATION_DEFAULT_SHEETS.map((spec) => {
    const fields = litigationFieldsForSheetKind(spec.kind, caseFields);
    return {
      sheetKind: spec.kind,
      sheetName: spec.name,
      headerRow: PLATFORM_LITIGATION_TEMPLATE_HEADER_ROW,
      dataStartRow: PLATFORM_LITIGATION_TEMPLATE_DATA_START_ROW,
      exportFields: fields.map((item, index) => {
        const base: LitigationPlanFieldMapping = {
          enabled: true,
          columnIndex: index,
          columnLetter: columnLetter(index),
          header: spec.kind === 'parties' && item.id === 'internalNo' ? '案件内部编号' : item.label,
          sourceField: item.id,
          valueMode: 'source',
        };
        if (spec.kind === 'cases' && item.id === 'latestProgress') {
          const progressSheet = LITIGATION_DEFAULT_SHEETS.find((entry) => entry.kind === 'progress');
          const content = LITIGATION_DETAIL_FIELDS.find((entry) => entry.id === 'progressContent');
          return {
            ...base,
            valueMode: 'reference',
            referenceSheetName: progressSheet?.name || '进展记录',
            referenceHeader: content?.label || '内容',
            sourceModuleId: 'legal-litigation-sheet-ref-v1',
            sourceModuleVersion: '1',
          };
        }
        return base;
      }),
    };
  });
}

function columnLetter(index: number): string {
  let value = index;
  let letters = '';
  while (value >= 0) {
    letters = String.fromCharCode((value % 26) + 65) + letters;
    value = Math.floor(value / 26) - 1;
  }
  return letters;
}
